import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Tooltip } from '@mui/material';
import { CheckCircle, Download, EmailOutlined } from '@mui/icons-material';
import axiosInstance from '../../utils/axios';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import { fileDownload } from '../../utils/fileDownload';

OrderFiles.propTypes = {
  editOrder: PropTypes.object,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function OrderFiles({ editOrder, open, close }) {
  const gridRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [orderFiles, setOrderFiles] = useState(null);

  useEffect(() => {
    setLoading(true);
    if (open) {
      axiosInstance.get(`/order/orderFiles?id=${editOrder.id}`)
        .then(res => setOrderFiles(res.data ?? []))
        .catch(() => enqueueSnackbar('Order files fetch failed', { variant: 'error' }))
        .finally(() => setLoading(false));
    }
    else setOrderFiles(null);
  }, [open]); 

  const downloadFile = (id, email, fileName) => {
    axiosInstance.get(`/order/downloadOrderFile?id=${id}&email=${email}`, { responseType: "blob" })
      .then(res => fileDownload(res.data, fileName))
      .catch(() => enqueueSnackbar('Order file fetch failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }

  const columns = useMemo(() => [
    {
      field: 'sendTo',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'type',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'sent',
      cellRenderer: (params) => params.value ? <CheckCircle color="primary" sx={{ mt: 1 }} /> : ""
    },
    {
      field: 'timeSent',
      cellRenderer: (params) => new Date(params.data.timeSent)
        .toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    },
    {
      field: 'actions',
      minWidth: 100,
      cellRenderer: (params) => <Stack direction="row" spacing={1}>
        <Tooltip title={"Download File"} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple onClick={() => downloadFile(params.data.id, false, params.data.fileName)} sx={{ px: 0 }}>
            <Download />
          </IconButton>
        </Tooltip>
        {params.data.emailFile && <Tooltip title={"Download Email"} arrow placement="top">
          <IconButton edge="end" color="secondary" disableRipple onClick={() => downloadFile(params.data.id, true, `${editOrder.id}.eml`)} sx={{ px: 0 }}>
            <EmailOutlined />
          </IconButton>
        </Tooltip>}       
      </Stack>
    }
  ], []);

  return <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
    <DialogContent>
      {!loading ? <Stack spacing={1}>
        <Box>
          <AGGridQuickFilterWrapper gridRef={gridRef} />
          <Box sx={{ height: "400px" }}>
            <AgGridReact ref={gridRef} {...AgGridDefaults} columnDefs={columns} rowData={orderFiles} />
          </Box>
        </Box>
      </Stack> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
    </DialogContent>
  </Dialog>;
}